import {
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Heading,
  Text,
  useDisclosure,
  VStack,
  Divider,
  FormControl,
  FormLabel,
  FormErrorMessage,
} from '@chakra-ui/react';
import React, { useMemo, useRef } from 'react';

import {
  date as commonDate,
  utils as commonUtils,
} from '@finley-technologies/common';

import { useCreditFacilitiesOrThrow } from '~/contexts/CreditFacilitiesProvider';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { PermissionGuard } from '~/core/PermissionGuard';
import { colors } from '~/packages/common-frontend/colors';
import { FullDateRangePicker } from '~/packages/common-frontend/components/DateRangePicker';
import { Icon } from '~/packages/common-frontend/components/icons';
import { IsolatedMultiSelect } from '~/packages/common-frontend/components/MultiSelect';
import { useGetCreditFacilitiesLoanRecordsQuery } from '~/packages/loans/requests/loans.gql.generated';
import {
  TransactionsFilterState,
  useDeriveTransactionsFiltersFromBackend,
} from '~/packages/transactions/hooks/transactionsHooks';

// styles
const dropdownButtonProps = {
  as: Button,
  minWidth: '10rem',
  variant: 'secondary-muted',
  fontWeight: 400,
  width: '100%',
};
const menuListProps = {
  bgColor: colors.background[100],
  py: '0',
};
const inputProps = {
  backgroundColor: 'white',
};
const menuItemOptionProps = {
  bgColor: colors.background[100],
  _hover: { bg: colors.background[500] },
};

type LoanOption = {
  id: string;
  label: string;
  value: string;
};

type TransactionsFiltersDrawerProps = TransactionsFilterState & {
  filtersToDisable?: { [key in keyof TransactionsFilterState]?: boolean };
  onFilterChange: (nextFilters: TransactionsFilterState) => void;
};
export const TransactionsFiltersDrawer = ({
  filtersToDisable,
  onFilterChange,
  ...initialFilter
}: TransactionsFiltersDrawerProps) => {
  const { user } = useUserAuthOrThrow();

  const drawerControls = useDisclosure();
  const { facilities } = useCreditFacilitiesOrThrow();

  const facilityLoanRecords = useGetCreditFacilitiesLoanRecordsQuery({
    variables: { organizationId: user.organization.id },
  });
  if (facilityLoanRecords.error) throw facilityLoanRecords.error;

  const uniqueLoanOptions = useMemo<LoanOption[]>(() => {
    if (!facilityLoanRecords.data) return [];

    const loanIdToOptionMap: Record<string, LoanOption> = {};
    for (const facility of facilityLoanRecords.data.creditFacilities) {
      for (const loan of facility.loanRecords) {
        let label = loan.id;

        if (loan.externalLoanId && loan.externalLoanId.length > 0) {
          label = `${loan.id} (${loan.externalLoanId})`;
        }

        loanIdToOptionMap[loan.id] = {
          id: loan.id,
          value: loan.id,
          label,
        };
      }
    }
    return Object.values(loanIdToOptionMap);
  }, [facilityLoanRecords.data]);

  const uniqueLoanIds = useMemo(() => {
    return uniqueLoanOptions.map((opt) => opt.id);
  }, [uniqueLoanOptions]);

  // Filters
  const transactionsFilters = useDeriveTransactionsFiltersFromBackend({
    creditFacilityIds: initialFilter.creditFacilityIds,
    loanIds: initialFilter.loanIds,
  });
  const { status, currentFilters, setCurrentFilters } = transactionsFilters;

  const filtersBtnRef = useRef(null);

  if (status === 'error') throw transactionsFilters.error;
  if (status === 'pending' || !facilityLoanRecords.data) return null; // we already show a loading skeleton in the table

  return (
    <>
      <Button
        variant="secondary"
        colorScheme="v2-aurora"
        ref={filtersBtnRef}
        onClick={drawerControls.onOpen}
        gap="0.5rem"
      >
        <Icon icon="filter" />
        Filters
      </Button>
      <Drawer
        placement="right"
        finalFocusRef={filtersBtnRef}
        size="xl"
        {...drawerControls}
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader textAlign="left" mt="1.5rem" px="3rem" pb="0">
            <Heading>Filters</Heading>
          </DrawerHeader>
          <Divider my="1.5rem" />
          <DrawerBody px="3rem">
            <VStack align="flex-start">
              <FormControl>
                <FormLabel>
                  <Heading fontSize="xl">Transaction Dates</Heading>
                  <Text color="gray">
                    Select a date range to apply the filter.
                  </Text>
                </FormLabel>
                <FullDateRangePicker // TODO: @haghanim add capability for customer to type date + fix border
                  onSelectDateRange={(newDateRange) => {
                    const [newStartDate, newEndDate] = newDateRange;
                    if (newStartDate && newEndDate) {
                      setCurrentFilters({
                        startDate: commonDate.dateToFullDateString(
                          newStartDate,
                        ),
                        endDate: commonDate.dateToFullDateString(newEndDate),
                      });
                    }
                  }}
                  currentDateRange={{
                    startDate: commonDate.fullDateStrToDate(
                      currentFilters.startDate,
                    ),
                    endDate: commonDate.fullDateStrToDate(
                      currentFilters.endDate,
                    ),
                  }}
                />
              </FormControl>

              <Divider my="1.5rem" />
              <FormControl
                isInvalid={currentFilters.creditFacilityIds.length === 0}
              >
                <FormLabel>
                  <Heading fontSize="xl">Credit Facilities</Heading>
                  <Text color="gray">
                    Select the credit facilities to apply the filter.
                  </Text>
                </FormLabel>
                <IsolatedMultiSelect
                  autoApplyOptions
                  buttonProps={{
                    ...dropdownButtonProps,
                    ...(filtersToDisable?.creditFacilityIds && {
                      isDisabled: true,
                    }),
                  }}
                  disabledOptionValues={
                    filtersToDisable?.creditFacilityIds
                      ? facilities.map((facility) => facility.id)
                      : undefined
                  }
                  initialSelections={currentFilters.creditFacilityIds}
                  inputProps={inputProps}
                  isSearchable
                  matchWidthPopupBreakpoint={400}
                  menuItemOptionProps={menuItemOptionProps}
                  menuListProps={menuListProps}
                  onApplyOptions={(creditFacilityIds) => {
                    setCurrentFilters({ creditFacilityIds });
                  }}
                  optionsHeader="Credit facilities"
                  options={facilities.map((facility) => ({
                    id: facility.id,
                    label: facility.name,
                    value: facility.id,
                  }))}
                  placeholderText="Select credit facilities"
                  showSelectedCount
                  titleText="All credit facilities"
                />
                <FormErrorMessage>
                  Select at least one facility.
                </FormErrorMessage>
              </FormControl>

              <PermissionGuard
                permissions={(context) =>
                  context.flags.productsLoansPermissions
                }
              >
                <Divider my="1.5rem" />
                <FormControl>
                  <FormLabel>
                    <Heading fontSize="xl">Loans</Heading>
                    <Text color="gray">
                      Select the loan(s) to apply the filter.
                    </Text>
                  </FormLabel>
                  <IsolatedMultiSelect
                    autoApplyOptions
                    buttonProps={dropdownButtonProps}
                    disabledOptionValues={
                      filtersToDisable?.loanIds ? uniqueLoanIds : undefined
                    }
                    inputProps={inputProps}
                    isSearchable
                    initialSelections={currentFilters.loanIds.filter((loanId) =>
                      uniqueLoanOptions.some((opt) => opt.id === loanId),
                    )}
                    matchWidthPopupBreakpoint={400}
                    menuItemOptionProps={menuItemOptionProps}
                    menuListProps={menuListProps}
                    onApplyOptions={(loanIds) => {
                      setCurrentFilters({ loanIds });
                    }}
                    options={uniqueLoanOptions}
                    optionsHeader="Loans"
                    placeholderText="Select loans"
                    showSelectedCount
                    titleText="All loans"
                  />
                </FormControl>
              </PermissionGuard>

              <Divider my="1.5rem" />
              <FormControl isInvalid={currentFilters.accountTypes.length === 0}>
                <FormLabel>
                  <Heading fontSize="xl">Account Types</Heading>
                  <Text color="gray">
                    Choose the account type(s) to apply the filter.
                  </Text>
                </FormLabel>
                <IsolatedMultiSelect
                  autoApplyOptions
                  buttonProps={dropdownButtonProps}
                  disabledOptionValues={
                    filtersToDisable?.accountTypes
                      ? transactionsFilters.uniqueTAccounts.map(
                          (accountType) => accountType,
                        )
                      : undefined
                  }
                  initialSelections={currentFilters.accountTypes}
                  inputProps={inputProps}
                  isSearchable
                  matchWidthPopupBreakpoint={400}
                  menuItemOptionProps={menuItemOptionProps}
                  menuListProps={menuListProps}
                  onApplyOptions={(accountTypes) => {
                    setCurrentFilters({ accountTypes });
                  }}
                  options={transactionsFilters.uniqueTAccounts.map(
                    (accountType) => ({
                      id: accountType,
                      label: commonUtils.toTitleCase(accountType),
                      value: accountType,
                    }),
                  )}
                  optionsHeader="Accounts"
                  placeholderText="Select account types"
                  showSelectedCount
                  titleText="All account types"
                />
                <FormErrorMessage>
                  Select at least one account type.
                </FormErrorMessage>
              </FormControl>
            </VStack>
          </DrawerBody>
          <Divider />
          <DrawerFooter justifyContent="space-between" m="1.5rem">
            <Button
              variant="secondary"
              colorScheme="v2-aurora"
              onClick={() => setCurrentFilters(initialFilter)}
            >
              Reset Filters
            </Button>
            <Button
              isDisabled={
                currentFilters.creditFacilityIds.length === 0 ||
                currentFilters.accountTypes.length === 0
              }
              variant="primary"
              colorScheme="v2-aurora"
              onClick={() => {
                onFilterChange(currentFilters);
                drawerControls.onClose();
              }}
            >
              Apply Filters
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  );
};
